import * as Sentry from '@sentry/browser'
import { Component, ReactNode, Suspense } from 'react'

import { AstraButton, AstraCallout, AstraParagraph, DeepinsightSymbolAnimated } from '~/astra'
import { useFlag } from '~/hooks/useFlag'

// Error boundary for lazy loading failures/signalling that a new component is available
export class LazyLoadErrorBoundary extends Component<{ children: ReactNode; componentName: string }, { hasError: boolean; error?: Error }> {
    constructor(props: { children: ReactNode; componentName: string }) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error) {
        Sentry.captureException(error, { tags: { component: this.props.componentName, operation: 'lazy_load' } })
    }

    render() {
        if (this.state.hasError) {
            const name = this.props.componentName.toLocaleLowerCase()
            const ariaLabel = name ? `Last ned oppdatering for ${name}` : 'Last ned oppdatering'

            return (
                <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-8">
                    <AstraCallout
                        color="info"
                        variant="filled"
                        size="large"
                        text={
                            <div className="flex w-[450px] flex-col gap-2">
                                <AstraParagraph variant="body" weight="medium">
                                    Ny versjon tilgjengelig!
                                </AstraParagraph>
                                <AstraParagraph variant="body-small" weight="regular">
                                    Denne siden har fått ny funksjonalitet siden sist. Oppdater siden for å få tilgang til siste versjon.
                                </AstraParagraph>
                            </div>
                        }
                    />
                    <AstraButton variant="primary" label="Last siden på nytt" onClick={() => window.location.reload()} aria-label={ariaLabel} />
                    <AstraParagraph variant="body-extra-small" className="text-gray-500">
                        Kontakt support hvis problemet fortsetter.
                    </AstraParagraph>
                </div>
            )
        }

        return this.props.children
    }
}

export const LoadingFallback = ({ componentName }: { componentName: string }) => (
    <div className="flex h-full w-full flex-col items-center justify-center bg-surface-base p-8">
        <DeepinsightSymbolAnimated forceAnimate delay={0} />
        <AstraParagraph variant="body-small" className="ml-7 text-gray-600">
            {componentName ? `Laster ${componentName.toLocaleLowerCase()}...` : 'Laster...'}
        </AstraParagraph>
    </div>
)

export const LazyComponentWrapper = ({ children, componentName, fallback }: { children: ReactNode; componentName: string; fallback?: ReactNode }) => {
    const useErrorBoundary = useFlag('lazy_load__error_boundary')
    const suspenseContent = <Suspense fallback={fallback || <LoadingFallback componentName={componentName} />}>{children}</Suspense>

    if (useErrorBoundary) {
        return <LazyLoadErrorBoundary componentName={componentName}>{suspenseContent}</LazyLoadErrorBoundary>
    }

    return suspenseContent
}
